import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Modal, Button, Badge, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCrown, faStar, faCheckCircle, faHeartbeat } from '@fortawesome/free-solid-svg-icons';
import { useLocation } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';
import packageJson from '../../package.json';

const API_URL = 'https://python-restful-api-250713.ew.r.appspot.com/api/version/';

const defaultFeatures = [
  "Support for Android 14 companion applications (HealthyMe & HealthSync).",
  "High-resolution wearable telemetry synchronization (Samsung Galaxy Ring, HRV, CGM).",
  "Personalized Cloud Dashboard (PCD) dedicated Kubernetes tenant orchestration.",
  "Transifex dynamic internationalization and multi-language support.",
  "Stable performance improvements and security patches."
];

const getStorageItem = (key) => {
  try {
    return typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
  } catch (e) {
    return null;
  }
};

const setStorageItem = (key, value) => {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(key, value);
    }
  } catch (e) {}
};

const WhatsNewDialog = ({
  isPcdUpgraded: propIsPcdUpgraded,
  authState: propAuthState,
  location: propLocation,
  user: propUser,
  onDismiss
}) => {
  // Use props if supplied, otherwise fallback to React Router and Okta Auth hooks
  const oktaAuthHook = useOktaAuth();
  const locationHook = useLocation();

  const authState = propAuthState !== undefined ? propAuthState : oktaAuthHook?.authState;
  const location = propLocation !== undefined ? propLocation : locationHook;

  const [show, setShow] = useState(false);
  const [mode, setMode] = useState('welcome'); // 'welcome' | 'pro_reminder'
  const [updates, setUpdates] = useState(defaultFeatures);
  const currentVersion = packageJson.version;

  const isAuthenticated = Boolean(authState?.isAuthenticated);
  const pathname = (location?.pathname || '').toLowerCase();
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isPcdSubdomain = /myclouddashboard/i.test(hostname);

  // Check if currently viewing PCD content
  const isPcdContent = isPcdSubdomain ||
    pathname.startsWith('/ecosystem/pcd') ||
    pathname === '/user' ||
    pathname === '/profile';

  // Check if PCD / Pro / Premium content or capability is applicable
  const searchParams = new URLSearchParams(
    location?.search || (typeof window !== 'undefined' ? window.location.search : '')
  );
  const isTierProParam = searchParams.get('tier') === 'pro' || searchParams.get('tier') === 'premium';
  const isUxPcdParam = searchParams.get('ux') === 'pcd' || searchParams.get('ux') === 'pro' || searchParams.get('ux') === 'premium';

  const hasProClaims = authState?.idToken?.claims?.subscription === 'Premium' ||
                       authState?.idToken?.claims?.tier === 'PRO' ||
                       authState?.idToken?.claims?.tier === 'PREMIUM';
  const isUserProTier = propUser?.tier === 'PRO' || propUser?.tier === 'PREMIUM';
  const isWindowProActive = typeof window !== 'undefined' && Boolean(window.PCD_PRO_ACTIVE);
  const isPcdAlphaActive = typeof window !== 'undefined' && Boolean(window.REACT_APP_PCD_ALPHA === 'true' || process.env.REACT_APP_PCD_ALPHA === 'true');
  const isConfigProFlavour = packageJson.config?.productFlavour?.toLowerCase()?.includes('pro') ||
                            packageJson.config?.enablePcdExtensions === 'true';

  const isPcdProApplicable = Boolean(
    propIsPcdUpgraded !== undefined ? propIsPcdUpgraded : (
      hasProClaims ||
      isUserProTier ||
      isWindowProActive ||
      isPcdAlphaActive ||
      isConfigProFlavour ||
      isTierProParam ||
      isUxPcdParam
    )
  );

  // Fetch release updates from API with fallback
  useEffect(() => {
    let isMounted = true;
    axios.get(API_URL, { params: { version: currentVersion }, timeout: 3000 })
      .then(res => {
        if (isMounted && res.data && Array.isArray(res.data.features) && res.data.features.length > 0) {
          setUpdates(res.data.features);
        }
      })
      .catch(() => {
        // Fallback already pre-set
      });

    return () => {
      isMounted = false;
    };
  }, [currentVersion]);

  // Evaluate whether to trigger the dialog
  useEffect(() => {
    const lastSeenVersion = getStorageItem('last_seen_version');
    const lastSeenPcdReminder = getStorageItem('pcd_pro_reminder_seen_version') || getStorageItem('last_seen_pcd_version');

    const isFirstTimeVisitor = lastSeenVersion !== currentVersion;
    const hasNewPcdCapabilities = lastSeenPcdReminder !== currentVersion;

    if (isAuthenticated && isPcdContent && isPcdProApplicable && hasNewPcdCapabilities) {
      // Pro/Premium customer accessing PCD content with new capabilities available
      setMode('pro_reminder');
      setShow(true);
    } else if (isFirstTimeVisitor) {
      // First-time visitor gets warm welcome and What's New
      setMode('welcome');
      setShow(true);
    } else {
      setShow(false);
    }
  }, [currentVersion, isAuthenticated, isPcdContent, isPcdProApplicable]);

  const handleDismiss = () => {
    if (mode === 'pro_reminder') {
      setStorageItem('pcd_pro_reminder_seen_version', currentVersion);
      setStorageItem('last_seen_pcd_version', currentVersion);
      setStorageItem('last_seen_version', currentVersion);
    } else {
      setStorageItem('last_seen_version', currentVersion);
      if (isPcdProApplicable && isPcdContent) {
        setStorageItem('pcd_pro_reminder_seen_version', currentVersion);
        setStorageItem('last_seen_pcd_version', currentVersion);
      }
    }
    setShow(false);
    if (typeof onDismiss === 'function') {
      onDismiss();
    }
  };

  if (!show) return null;

  return (
    <div className="modal-overlay" data-testid="whats-new-modal-overlay">
      <Modal
        show={show}
        onHide={handleDismiss}
        centered
        size="lg"
        className="whats-new-modal"
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton className={mode === 'pro_reminder' ? "bg-dark text-white border-warning" : "bg-warning text-white"}>
          <Modal.Title className="fs-5 fw-bold d-flex align-items-center gap-2">
            {mode === 'pro_reminder' ? (
              <>
                <FontAwesomeIcon icon={faCrown} className="text-warning" />
                <span>Personalized Cloud Dashboard (<abbr title="Personalized Cloud Dashboard">PCD</abbr>)</span>
                <Badge bg="warning" text="dark" className="ms-2">PRO &amp; Premium</Badge>
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faStar} className="text-white" />
                <span>Welcome to HealthDash | What's New in v{currentVersion}</span>
              </>
            )}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="whats-new-body p-4">
          {mode === 'pro_reminder' ? (
            <>
              {/* Pro Capabilities Welcome Banner */}
              <Alert variant="warning" className="border-warning mb-3">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <FontAwesomeIcon icon={faCrown} className="fs-4 text-warning" />
                  <h5 className="mb-0 fw-bold">Pro &amp; Premium Capabilities Unlocked</h5>
                </div>
                <p className="small mb-0 text-dark">
                  Thank you for supporting HealthDash through Google Play Store (HealthyMe Pro / HealthSync Pro) or our distribution channels!
                  Your Pro access grants you full access to advanced telemetry synchronization, dedicated private cloud infrastructure, and real-time medical-grade analytics.
                </p>
              </Alert>

              {/* Pro Capabilities Highlights */}
              <h6 className="fw-bold text-dark mb-2">
                <FontAwesomeIcon icon={faCheckCircle} className="text-success me-2" />
                What you can do with your Pro access:
              </h6>
              <ul className="small text-muted mb-3">
                <li><strong>Dedicated Private K8s Cluster:</strong> Isolated tenant namespace with real-time zero-drift health telemetry.</li>
                <li><strong>High-Resolution Medical Telemetry:</strong> Live tracking for Continuous Glucose Monitors (CGM), Blood Pressure Monitors, HRV, and Smart Scales.</li>
                <li><strong>Android Pro Companion Apps:</strong> Bi-directional synchronization with HealthyMe Pro (<code>com.linardsl.libhacontimig.hm.finalPro</code>) and HealthSync Pro (<code>com.linardsl.libhacontimig.finalPro</code>).</li>
                <li><strong>Advanced Observability:</strong> Real-time infrastructure monitoring, CloudWatch telemetry, and automated deployment stack observability.</li>
              </ul>

              {/* Alpha Warning Alert */}
              <div className="alert-pcd-alpha alert alert-info py-2 px-3 small rounded-3 mb-3">
                <hr className="my-1" />
                <strong><abbr title="Personalized Cloud Dashboard">PCD</abbr> Upgrade Detected:</strong>{' '}
                Please note that the Personalized Cloud Dashboard (<abbr title="Personalized Cloud Dashboard">PCD</abbr>) experience on top of <abbr title="Cloud Dashboard">CD</abbr> is currently in
                an Alpha stage. Certain synchronization triggers may not be fully supported yet as we stabilize the npm-published package integration.
              </div>

              <h6 className="fw-bold text-dark mb-2">Release Highlights (v{currentVersion}):</h6>
              <ul className="small text-muted mb-0">
                {updates.map((u, i) => <li key={i}>{u}</li>)}
              </ul>
            </>
          ) : (
            <>
              {/* Warm Welcome Greeting */}
              <div className="welcome-banner mb-3 p-3 bg-light rounded-3 border">
                <h5 className="fw-bold text-dark mb-1">
                  <FontAwesomeIcon icon={faHeartbeat} className="text-danger me-2" />
                  Welcome to HealthDash &amp; The Cloud Dashboard!
                </h5>
                <p className="small text-muted mb-0">
                  Your comprehensive platform for fitness telemetry, wearable device synchronization (Samsung Health, Huawei Health, Google Fit), and cloud wellness tracking.
                </p>
              </div>

              {/* What's New Updates */}
              <h6 className="fw-bold text-dark mb-2">What's New in v{currentVersion}:</h6>
              <ul className="small text-muted mb-3">
                {updates.map((u, i) => <li key={i}>{u}</li>)}
                {updates.length === 0 && <li>Stable performance improvements and security patches.</li>}
              </ul>

              {propIsPcdUpgraded && (
                <div className="alert-pcd-alpha alert alert-info py-2 px-3 small rounded-3 mb-2">
                  <hr className="my-1" />
                  <strong><abbr title="Personalized Cloud Dashboard">PCD</abbr> Upgrade Detected:</strong>{' '}
                  Please note that the Personalized Cloud Dashboard (<abbr title="Personalized Cloud Dashboard">PCD</abbr>) experience on top of <abbr title="Cloud Dashboard">CD</abbr> is currently in
                  an Alpha stage. Certain synchronization triggers may not be fully supported yet
                  as we stabilize the npm-published package integration.
                </div>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer className="d-flex justify-content-between align-items-center bg-light">
          <span className="small text-muted">With Love from LATVIA // CD and PCD, powered by HealthDash Ecosystem v{currentVersion} // Brought to you by Linards Liepins (HolimaX)</span>
          <Button
            variant={mode === 'pro_reminder' ? "warning" : "primary"}
            className="btn-confirm fw-bold text-white px-4"
            onClick={handleDismiss}
            data-testid="whats-new-dismiss-btn"
          >
            {mode === 'pro_reminder' ? "Explore Pro Features!" : "Great!"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default WhatsNewDialog;